import React, { useState } from 'react'
import { stageMeta, isYesLabel, isNoLabel, slugify } from './remoteUnlockApi'
import StageForm from './StageForm'
import './remoteUnlock.css'

const toneOf = (label) => (isYesLabel(label) ? 'ok' : isNoLabel(label) ? 'bad' : 'neutral')

/** read-only list of a stage's submitted answers */
function StageAnswers({ rows }) {
  return (
    <div className="ru-stage-body ru-cl-body">
      {rows.map((r, i) => {
        const value = r.optionLabel || r.description || '—'
        const note = r.optionLabel && r.description ? r.description : ''
        return (
          <div key={`${r.questionId}-${i}`} className="ru-cl-row">
            <span className="ru-answer-num">{i + 1}</span>
            <div className="ru-cl-main">
              <div className="ru-cl-q">{r.questionText || `Question ${i + 1}`}</div>
              {note && <div className="ru-cl-note">{note}</div>}
            </div>
            <span className={`ru-chip ${r.optionLabel ? toneOf(value) : 'neutral'}`}>{value}</span>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Remote Session tab.
 *  • every stage's submitted answers are visible to everyone (read-only)
 *  • the first unfilled stage is the "current" one — its form (from get-checklist)
 *    is shown only if the agent's remote_session_level includes that level
 */
export default function RemoteSessionTab({
  fetchJson, ticketId, info, stationCode, checklist, loading, error, levels, ticketClosed = false, onSaved,
}) {
  const [banner, setBanner] = useState(null)
  const [collapsed, setCollapsed] = useState({})
  const stages = checklist?.stages || ['l1', 'l2', 'l3']
  const byStage = checklist?.byStage || {}
  const filled = (s) => (byStage[s] || []).length > 0
  const current = stages.find(s => !filled(s)) || null
  // once the ticket is closed nobody can fill a stage, whatever their access
  const canFill = (s) => !ticketClosed && Array.isArray(levels) && levels.includes(s)
  const subSlug = info?.sub_category_slug || slugify(info?.sub_category)

  const statusOf = (s) => {
    if (filled(s)) return { cls: 'done', label: 'Completed' }
    if (s !== current) return { cls: '', label: 'Not started' }
    if (ticketClosed) return { cls: '', label: 'Ticket closed' }
    if (levels === null) return { cls: 'active', label: 'Checking access…' }
    return canFill(s) ? { cls: 'active', label: 'Active' } : { cls: 'wait', label: `Awaiting ${stageMeta(s).level} agent` }
  }

  const handleSaved = (msg, ok = true) => { setBanner({ ok, msg }); onSaved?.() }

  return (
    <div className="td-main ru-tab">
      <div className="ru-journey-title">Remote Session Journey</div>
      <div className="ru-pills">
        {stages.map((s, i) => (
          <React.Fragment key={s}>
            {i > 0 && <span className="ru-pill-sep">›</span>}
            <span className={`ru-pill ${filled(s) ? 'done' : s === current ? 'active' : ''}`}>
              {filled(s) ? '✓ ' : s === current ? '● ' : ''}Stage {stageMeta(s).roman}{s === 'l3' ? ' · CNC' : ''}
            </span>
          </React.Fragment>
        ))}
      </div>

      {banner && (
        <div className={`ru-banner ${banner.ok ? 'ok' : 'warn'}`}>
          <span>{banner.msg}</span>
          <button type="button" onClick={() => setBanner(null)} aria-label="Dismiss">×</button>
        </div>
      )}
      {ticketClosed && (
        <div className="ru-banner closed">
          <span>🔒 This ticket is closed — the checklist is read-only and no further actions can be taken.</span>
        </div>
      )}
      {loading && !checklist && <div className="ru-muted">Loading checklist…</div>}
      {error && <div className="ru-error">{error}</div>}

      {stages.map(s => {
        const meta = stageMeta(s)
        const st = statusOf(s)
        const rows = byStage[s] || []
        const isCurrent = s === current
        const showForm = isCurrent && canFill(s)
        const open = filled(s) ? !collapsed[s] : isCurrent
        return (
          <div key={s} className={`ru-stage ${filled(s) ? 'done' : showForm ? 'active' : isCurrent ? '' : 'muted'}`}>
            <button
              type="button" className="ru-stage-head ru-stage-toggle"
              onClick={() => filled(s) && setCollapsed(c => ({ ...c, [s]: !c[s] }))}
              aria-expanded={open}
              style={filled(s) ? undefined : { cursor: 'default' }}
            >
              <span className="ru-stage-num">{meta.roman}</span>
              <span className="ru-stage-titles">
                <span className="ru-stage-title">{meta.title}</span>
                <span className="ru-stage-sub">
                  {meta.sub}{filled(s) ? ` · ${rows.length} answer${rows.length !== 1 ? 's' : ''}` : ''}
                </span>
              </span>
              <span className="ru-level">{meta.level}</span>
              <span className={`ru-status ${st.cls}`}>{st.label}</span>
              {filled(s) && <span className={`ru-caret${open ? ' open' : ''}`} aria-hidden>⌄</span>}
            </button>

            {filled(s) && open && <StageAnswers rows={rows} />}

            {showForm && (
              <StageForm
                fetchJson={fetchJson} ticketId={ticketId} stage={s}
                subCategorySlug={subSlug} stationCode={stationCode} previous={byStage}
                onSaved={handleSaved}
              />
            )}

            {isCurrent && !showForm && levels !== null && !ticketClosed && (
              <div className="ru-stage-body">
                <div className="ru-muted">
                  This stage has to be filled by an agent with {meta.level} remote-session access.
                  You can view the previous stages above.
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
